import { useState, useEffect } from "react";
import {
  Button,
  Grid,
  CircularProgress,
  Checkbox,
  Autocomplete,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

import { getDependencies, getLocations } from "../../services/LegalFileService";
import { getLawyers } from "../../services/LawyerService";
import { exportIndividualLiquidationSheet } from "../../services/IndividualLiquidationService";
import CardHeader from "../General/CardHeader";
import GlassCard from "../General/GlassCard";
import LoadingState from "../General/LoadingState";
import { useToast } from "../../context/ToastContext";

export default function ReportIndividualLiquidationSheet() {
  const { showToast } = useToast();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [dependencies, setDependencies] = useState([]);
  const [locations, setLocations] = useState([]);
  const [lawyers, setLawyers] = useState([]);

  const [selectedDependencies, setSelectedDependencies] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedLawyers, setSelectedLawyers] = useState([]);

  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  useEffect(() => {
    async function fetchFilters() {
      setLoadingOptions(true);
      try {
        const [resDep, resLoc, resLaw] = await Promise.all([
          getDependencies(),
          getLocations(),
          getLawyers(),
        ]);
        setDependencies(resDep.data.data || []);
        setLocations(resLoc.data.data || []);
        setLawyers(resLaw.data.data || []);
      } catch (e) {
        showToast("Error al cargar filtros.", "error");
      } finally {
        setLoadingOptions(false);
      }
    }
    fetchFilters();
  }, [showToast]);

  const handleGenerate = async () => {
    if (startDate && endDate && startDate.isAfter(endDate)) {
      showToast("La fecha de inicio no puede ser posterior a la de fin.", "error");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        dependencies: selectedDependencies,
        locations: selectedLocations,
        lawyers: selectedLawyers,
      };
      if (startDate) payload.start_date = startDate.format("YYYY-MM-DD");
      if (endDate) payload.end_date = endDate.format("YYYY-MM-DD");

      const response = await exportIndividualLiquidationSheet(payload);
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 19).replace(/:/g, "-").replace("T", "_");
      a.download = `planilla_liquidaciones_comunes_${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      showToast("Ocurrió un error al generar la planilla.", "error");
    } finally {
      setLoading(false);
    }
  };

  if (loadingOptions) return <LoadingState text="Cargando filtros..." />;

  return (
    <Grid container justifyContent="center" sx={{ mt: 4 }}>
      <Grid size={{ xs: 12, lg: 10 }}>
        <GlassCard>
          <CardHeader
            icon={<PersonRoundedIcon />}
            title="Planilla de Liquidaciones Comunes"
            description="Expedientes comunes con liquidación común activa: totales por categoría, intereses, punitorios e IVA."
          />

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, lg: 6 }}>
              <DatePicker
                label="Liquidación creada desde"
                value={startDate}
                onChange={setStartDate}
                format="DD/MM/YYYY"
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }}>
              <DatePicker
                label="Liquidación creada hasta"
                value={endDate}
                onChange={setEndDate}
                format="DD/MM/YYYY"
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Autocomplete
                multiple
                options={dependencies}
                disableCloseOnSelect
                getOptionLabel={(o) => o.name || o}
                value={selectedDependencies}
                onChange={(e, v) => setSelectedDependencies(v)}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox icon={icon} checkedIcon={checkedIcon} checked={selected} />
                    {option.name || option}
                  </li>
                )}
                renderInput={(params) => <TextField {...params} label="Juzgados" placeholder="Seleccionar..." />}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Autocomplete
                multiple
                options={locations}
                disableCloseOnSelect
                getOptionLabel={(o) => o.name || o}
                value={selectedLocations}
                onChange={(e, v) => setSelectedLocations(v)}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox icon={icon} checkedIcon={checkedIcon} checked={selected} />
                    {option.name || option}
                  </li>
                )}
                renderInput={(params) => <TextField {...params} label="Ubicaciones" placeholder="Seleccionar..." />}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Autocomplete
                multiple
                options={lawyers}
                disableCloseOnSelect
                getOptionLabel={(o) => `${o.name} ${o.lastname}` || o}
                value={selectedLawyers}
                onChange={(e, v) => setSelectedLawyers(v)}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox icon={icon} checkedIcon={checkedIcon} checked={selected} />
                    {`${option.name} ${option.lastname}` || option}
                  </li>
                )}
                renderInput={(params) => <TextField {...params} label="Abogados" placeholder="Seleccionar..." />}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Button variant="contained" fullWidth onClick={handleGenerate} disabled={loading}>
                {loading ? <CircularProgress size={24} color="inherit" /> : "Generar Planilla"}
              </Button>
            </Grid>
          </Grid>
        </GlassCard>
      </Grid>
    </Grid>
  );
}
